'use client';
import { HitType } from '@/modules/common/lib/abtastyStub';

import { DataLayerWhere } from '@/modules/cart/services/data-layer-events/add-to-cart';
import Image from '@/modules/common/components/Image';
import MarkdownRenderer from '@/modules/common/components/MarkdownRenderer';
import useFeatureFlag from '@/modules/common/hooks/useFeatureFlag';
import { useAppSelector } from '@/modules/common/hooks/useStore';
import {
  DataLayerFeature,
  addToWishlistDataLayerEvent,
} from '@/modules/product/services/data-layer-events/add-to-wishlist';
import { CollectionDataLayer } from '@/modules/product/services/data-layer-events/utils';
import { AddToShoppingListButtonRegular } from '@/modules/shopping-list/components/AddToShoppingListButtonRegular';
import { ShoppingListSaveType } from '@/modules/shopping-list/hooks/useShoppingListActions';
import { SaveType } from '@/modules/shopping-list/models/shoppingListTextItem.interface';
import { selectShoppingListProducts } from '@/modules/shopping-list/state/shoppingListSlice';

import { AffiliateFieldsFragment } from '@/gql/__generated/graphql.types';

type Props = {
  collection: AffiliateFieldsFragment;
};
export const AffiliateHeader = ({ collection }: Props) => {
  const shoppingListProducts = useAppSelector(selectShoppingListProducts);
  const { sendFeatureFlagTracking } = useFeatureFlag();
  const skus = collection?.products?.map((product: { sku: string }) => product.sku) || [];

  const { slug, collectionName: title, newBaseImage } = collection;

  const shoppingListData = {
    name: title ?? '',
    key: slug ?? '',
    saveType: SaveType.Collection,
  };

  const wishlistTrackingCallback = () => {
    addToWishlistDataLayerEvent({
      feature: DataLayerFeature.Collections,
      method: 'Single',
      type: ShoppingListSaveType.Collection,
      product: {
        slug: slug ?? '',
        title: title ?? '',
      } satisfies CollectionDataLayer,
      where: DataLayerWhere.Collections,
      shoppingListProducts,
      item_list_type: 'Editorial collection',
      item_list_name: title ?? '',
      item_list_id: slug ?? '',
    });

    sendFeatureFlagTracking({
      type: HitType.EVENT,
      category: 'Action Tracking',
      action: 'Collection Saved',
      label: 'Affiliate Collection',
    });
  };

  return (
    <header className="mx-auto w-full px-4 md:px-8">
      <div className="relative lg:flex lg:items-center lg:gap-8">
        <div className="text-center lg:mt-5 lg:text-left xl:mt-0">
          <h1
            className="text-[24px] font-bold xs:text-[28px] lg:text-4xl"
            data-testid="collection-name"
          >
            {collection?.collectionName || ''}
          </h1>

          <MarkdownRenderer
            className="mx-auto mb-6 self-center md:mb-8 md:max-w-[544px] lg:max-w-[912px] lg:text-2xl"
            data-testid="collection-description"
          >
            {collection?.description}
          </MarkdownRenderer>

          <div className="mb-6 md:mb-8 lg:mb-0">
            <AddToShoppingListButtonRegular
              saveType={ShoppingListSaveType.Affiliate}
              textLineItem={shoppingListData}
              image={newBaseImage?.url ?? ''}
              className="w-full md:w-auto"
              sku={skus[0] || undefined}
              callbacks={{ afterAdd: wishlistTrackingCallback }}
              data-testid="add-to-shopping-list-button"
            />
          </div>
        </div>

        <div className="w-full max-w-[700px] lg:max-w-[800px]">
          {collection?.newBaseImage?.url && (
            <Image
              width={collection.newBaseImage?.width ?? 800}
              height={collection.newBaseImage?.height ?? 400}
              src={collection.newBaseImage?.url}
              alt="Collection Banner"
              className="w-full rounded-xxl object-contain"
            />
          )}
        </div>
      </div>
    </header>
  );
};
